import { AnalyticsProvider } from '@inboundx/shared/analytics';
import { ChatStorageService, FetchNetworkAdapter } from '@inboundx/shared/platform';
import { DisplayController } from '@inboundx/shared/widget';
import { CheckCircle, Info, PartyPopper, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { ConfigurableStorageAdapter } from '../adapters/ConfigurableStorageAdapter';
import { ClientConfigProvider } from '../components/ClientConfigProvider';
import { EventViewerPanel } from '../components/EventViewerPanel';
import { isPreprodParentWindowEventsEnabled } from '../components/FeaturesTab';
import { getConfig } from '../config';
import { API_ENDPOINTS } from '../constants/endpoints';
import { useAppContext } from '../contexts/AppContext';

export default function PostConversionTestPage() {
    const { selectedEndpoint, customEndpoint, useCustomEndpoint, selectedClient } = useAppContext();

    const [networkAdapter] = useState(new FetchNetworkAdapter());

    const getCurrentApiUrl = () => {
        if (useCustomEndpoint && customEndpoint) {
            return customEndpoint;
        }
        return selectedEndpoint?.url || API_ENDPOINTS[0].url;
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: getCurrentApiUrl closes over the listed deps — listing them instead of the unstable function reference
    const storageAdapter = useMemo(
        () => new ConfigurableStorageAdapter(getCurrentApiUrl),
        [selectedEndpoint, customEndpoint, useCustomEndpoint]
    );

    const currentApiUrl = getCurrentApiUrl();

    // Extract URL params to show what would be captured
    const urlParams =
        typeof window !== 'undefined'
            ? Object.fromEntries(new URLSearchParams(window.location.search))
            : {};

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                        <PartyPopper className="h-8 w-8 text-green-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Post-Conversion Qualification Test
                    </h1>
                    <p className="text-lg text-gray-600">
                        Simulates a thank-you page after form submission
                    </p>
                    {selectedClient && (
                        <button
                            type="button"
                            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors"
                            onClick={() => {
                                ChatStorageService.clearPostConversionComplete(
                                    selectedClient.domain
                                );
                                window.location.reload();
                            }}
                        >
                            <RotateCcw className="h-3 w-3" />
                            Reset post-conversion completion
                        </button>
                    )}
                </div>

                {/* Info Card */}
                <Card className="p-6 bg-green-50 border-green-200 mb-8">
                    <div className="flex items-start">
                        <Info className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="text-sm text-green-800">
                            <p className="font-semibold mb-2">Post-Conversion Behavior:</p>
                            <ul className="space-y-1 list-disc list-inside">
                                <li>Widget auto-opens after 1 second delay</li>
                                <li>Welcome message from form config is displayed</li>
                                <li>Agent asks qualification questions in configured order</li>
                                <li>Closing the widget prevents re-opening (sessionStorage)</li>
                                <li>URL parameters are captured as postConversionContext</li>
                            </ul>
                            <p className="mt-3 text-green-600">
                                <strong>Setup:</strong> The selected client must have a
                                post-conversion form with{' '}
                                <code className="bg-green-100 px-1 rounded">trigger.pages</code>{' '}
                                matching{' '}
                                <code className="bg-green-100 px-1 rounded">/thank-you*</code>. Add{' '}
                                <code className="bg-green-100 px-1 rounded">
                                    ?email=test@example.com
                                </code>{' '}
                                to the URL to simulate captured parameters.
                            </p>
                        </div>
                    </div>
                </Card>

                {/* Thank-you page simulation */}
                <Card className="p-8 bg-white/90 border-green-200 mb-6">
                    <div className="text-center">
                        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Thank you for your request!
                        </h2>
                        <p className="text-gray-600 mb-6">
                            We have received your demo request and will get back to you shortly. In
                            the meantime, our assistant can help collect a few details to prepare
                            your demo.
                        </p>
                        <div className="inline-flex items-center px-4 py-2 bg-green-50 rounded-full text-sm text-green-700">
                            Confirmation sent to your email
                        </div>
                    </div>
                </Card>

                {/* URL Parameters */}
                {Object.keys(urlParams).length > 0 && (
                    <Card className="p-4 bg-amber-50 border-amber-200 mb-6">
                        <h3 className="text-sm font-semibold text-amber-800 mb-2">
                            Captured URL Parameters (postConversionContext)
                        </h3>
                        <div className="space-y-1 text-xs text-amber-700 font-mono">
                            {Object.entries(urlParams).map(([key, value]) => (
                                <div key={key}>
                                    <strong>{key}:</strong> {value}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Placeholder content */}
                <div className="space-y-4">
                    {['What happens next?', 'Prepare for your demo', 'Explore our resources'].map(
                        (title) => (
                            <Card key={title} className="p-5 bg-white/60">
                                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                    {title}
                                </h3>
                                <p className="text-gray-500">
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                                    eiusmod tempor incididunt ut labore et dolore magna aliqua.
                                </p>
                            </Card>
                        )
                    )}
                </div>

                {/* Config info */}
                {selectedClient && (
                    <Card className="mt-6 p-4 bg-gray-50 border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                            Current Configuration
                        </h3>
                        <div className="space-y-1 text-xs text-gray-600">
                            <div>
                                <strong>Client:</strong> {selectedClient.name} (
                                {selectedClient.domain})
                            </div>
                            <div>
                                <strong>API URL:</strong> <code>{currentApiUrl}</code>
                            </div>
                            <div>
                                <strong>Page path:</strong>{' '}
                                <code>
                                    {typeof window !== 'undefined' ? window.location.pathname : ''}
                                </code>
                            </div>
                            <div>
                                <strong>Post-conversion detection:</strong> automatic (via
                                trigger.pages config match)
                            </div>
                        </div>
                    </Card>
                )}
            </div>

            {/* Widget — DisplayController auto-detects isPostConversion from config */}
            {selectedClient && (
                <ClientConfigProvider
                    client={selectedClient}
                    apiUrl={currentApiUrl}
                    apiKey={getConfig().apiKey}
                >
                    <AnalyticsProvider>
                        <EventViewerPanel isVisible={isPreprodParentWindowEventsEnabled()} />
                        <DisplayController
                            apiUrl={currentApiUrl}
                            apiKey={getConfig().apiKey}
                            siteName={selectedClient.siteName}
                            domain={selectedClient.domain}
                            storageAdapter={storageAdapter}
                            networkAdapter={networkAdapter}
                            forceDisplay={true}
                            isPostConversion={true}
                            visitorIp={getConfig().visitorIp}
                        />
                    </AnalyticsProvider>
                </ClientConfigProvider>
            )}
        </div>
    );
}
